import { useCallback, useEffect, useMemo, useState } from 'react';

import { StyledBody } from 'baseui/card';
import { FIELD_CONFIG, OPERATOR_CONFIG } from 'configs/columns';
import { CashFlowsFormWithDataDisplay } from 'features/drilldown/cashflows/CashFlows';
import { CashFlowsValidValues } from 'features/drilldown/cashflows/configurations/cashflow-inputs';
import { getUseDealCashflows } from 'features/drilldown/cashflows/gql/useDealCashflows_gql';
import { DateTime } from 'luxon';
import { Tab, Tabs } from 'ui-kit';
import { useDebouncedCallback } from 'use-debounce';
import { isNotNullOrUndefined } from 'utils/typeUtils';

import { useMutation, useQuery } from '@apollo/client';

import {
  DefaultAssumptionType,
  DealRole,
  PrepaymentAssumptionType,
  StipulationFieldName,
  StipulationToggleName,
} from '__generated__/globalTypes';

import {
  GetCarves,
  GetCarvesVariables,
  GetCarves_deal_carves,
} from 'query/__generated__/GetCarves';
import { GET_CARVES } from 'query/getCarves';

import { BiddingAndPricingControllerCard_PlaceBidCard } from './__generated__/BiddingAndPricingControllerCard';
import {
  ToggleCarveStipulation,
  ToggleCarveStipulationVariables,
} from './__generated__/ToggleCarveStipulation';
import {
  UpdateCarveStipulation,
  UpdateCarveStipulationVariables,
} from './__generated__/UpdateCarveStipulation';
import {
  UpdateCustomStipulations,
  UpdateCustomStipulationsVariables,
} from './__generated__/UpdateCustomStipulations';
import { BidActionButtons } from './BidActionButtons';
import { BidDetails } from './BidDetails';
import BiddingPricingContext from './BiddingPricingContext';
import BidStipulations from './BidStipulations';
import {
  TOGGLE_CARVE_STIPULATION,
  UPDATE_CARVE_STIPULATIONS,
  UPDATE_CUSTOM_STIPULATIONS,
} from './BidStipulations.fragments';
import CarveTable from './CarveTable';
import { PREDEFINED_PARTICIPATION_PERCENTS } from './constants';
import { DealRoomCard } from '../DealRoom.styles';
import { useDealContext } from './hooks/useDealContext';
import YieldMatrix from './YieldMatrix/YieldMatrix';
import { BuyerBidHistoryAccordion } from '../ActiveTrades/BuyerBidHistoryAccordion';
import { adaptPlaceBidCardBid } from '../ActiveTrades/shared/bidUtils';

const useUpdateStips = (dealId: string) => {
  const { offeringId, buyerCompanyId } = useDealContext(dealId);

  const refetchQueries = useMemo(() => {
    const getCarvesVariables: GetCarvesVariables = {
      deal_id: dealId,
    };
    return [{ query: GET_CARVES, variables: getCarvesVariables }];
  }, [dealId]);

  const [updateCarveStipulations] = useMutation<
    UpdateCarveStipulation,
    UpdateCarveStipulationVariables
  >(UPDATE_CARVE_STIPULATIONS, {
    refetchQueries,
  });

  const [updateCustomStipulations] = useMutation<
    UpdateCustomStipulations,
    UpdateCustomStipulationsVariables
  >(UPDATE_CUSTOM_STIPULATIONS, {
    refetchQueries,
  });

  const [toggleCarveStipulation] = useMutation<
    ToggleCarveStipulation,
    ToggleCarveStipulationVariables
  >(TOGGLE_CARVE_STIPULATION, {
    refetchQueries,
  });

  // Create wrapped functions that include the required parameters
  const wrappedUpdateCarveStipulations = useCallback(
    (variables: Omit<UpdateCarveStipulationVariables, 'offeringId' | 'buyerCompanyId'>) => {
      if (!offeringId || !buyerCompanyId) {
        console.error('Missing offeringId or buyerCompanyId for carve stipulation update');
        return Promise.reject(new Error('Missing required parameters'));
      }
      return updateCarveStipulations({
        variables: {
          ...variables,
          offeringId,
          buyerCompanyId,
        },
      });
    },
    [updateCarveStipulations, offeringId, buyerCompanyId],
  );

  const wrappedUpdateCustomStipulations = useCallback(
    (variables: Omit<UpdateCustomStipulationsVariables, 'offeringId' | 'buyerCompanyId'>) => {
      if (!offeringId || !buyerCompanyId) {
        console.error('Missing offeringId or buyerCompanyId for custom stipulations update');
        return Promise.reject(new Error('Missing required parameters'));
      }
      return updateCustomStipulations({
        variables: {
          ...variables,
          offeringId,
          buyerCompanyId,
        },
      });
    },
    [updateCustomStipulations, offeringId, buyerCompanyId],
  );

  const wrappedToggleCarveStipulation = useCallback(
    (variables: Omit<ToggleCarveStipulationVariables, 'offeringId' | 'buyerCompanyId'>) => {
      if (!offeringId || !buyerCompanyId) {
        console.error('Missing offeringId or buyerCompanyId for carve stipulation toggle');
        return Promise.reject(new Error('Missing required parameters'));
      }
      return toggleCarveStipulation({
        variables: {
          ...variables,
          offeringId,
          buyerCompanyId,
        },
      });
    },
    [toggleCarveStipulation, offeringId, buyerCompanyId],
  );

  const debouncedUpdateCarveStipulations = useDebouncedCallback(
    wrappedUpdateCarveStipulations,
    1000,
  );

  const debouncedUpdateCustomStipulations = useDebouncedCallback(
    wrappedUpdateCustomStipulations,
    1000,
  );

  return {
    toggleCarveStipulation: useDebouncedCallback(wrappedToggleCarveStipulation, 250),
    updateCarveStipulations: debouncedUpdateCarveStipulations,
    updateCustomStipulations: debouncedUpdateCustomStipulations,
    // Add methods to flush pending updates synchronously
    flushCarveStipulations: () => debouncedUpdateCarveStipulations.flush(),
    flushCustomStipulations: () => debouncedUpdateCustomStipulations.flush(),
    // Add direct (non-debounced) update method for immediate execution
    updateCarveStipulationsImmediate: wrappedUpdateCarveStipulations,
  };
};

const useCarvesAndBids = (dealId: string, sellerParticipationRate?: number | null) => {
  const {
    toggleCarveStipulation,
    updateCarveStipulations,
    updateCustomStipulations,
    flushCarveStipulations,
    flushCustomStipulations,
    updateCarveStipulationsImmediate,
  } = useUpdateStips(dealId);
  const [bidDetailsMap, setBidDetailsMap] = useState<Record<string, BidDetails>>({});
  const [selectedCarveId, setSelectedCarveId] = useState<string | null>(null);

  const { data: carvesData } = useQuery<GetCarves, GetCarvesVariables>(GET_CARVES, {
    variables: {
      deal_id: dealId,
    },
    pollInterval: 5000,
  });

  const carves: GetCarves_deal_carves[] = useMemo(() => {
    const carves = (carvesData?.deal?.carves || []).filter(isNotNullOrUndefined);

    setBidDetailsMap((prev) => {
      const newBidDetailsMap: Record<string, BidDetails> = carves.reduce(
        (acc, carve) => ({
          ...acc,
          [carve.id]: (prev[carve.id] && {
            ...prev[carve.id],
            stipulations: {
              ...carve.stipulations,
              ...prev[carve.id].stipulations,
            },
          }) || {
            carveId: carve.id,
            carve,
            cpr: 0,
            cdr: 0,
            lsr: 0,
            delay: 0,
            participationPercent: (() => {
              const effectiveTransferAmount =
                carve.stipulations?.transfer_amount ?? sellerParticipationRate ?? 100;
              const knownValues = [...PREDEFINED_PARTICIPATION_PERCENTS, 100];
              return {
                isCustom: !knownValues.includes(effectiveTransferAmount),
                value: `${effectiveTransferAmount}`,
                draftValue: knownValues.includes(effectiveTransferAmount)
                  ? ''
                  : `${effectiveTransferAmount}`,
              };
            })(),
            armRateProjection: 'flat',
            stipulations: {
              ...carve.stipulations,
              transfer_amount:
                carve.stipulations?.transfer_amount ?? sellerParticipationRate ?? 100,
              custom1: carve.stipulations?.custom_stipulations?.[0] || '',
              custom2: carve.stipulations?.custom_stipulations?.[1] || '',
              custom3: carve.stipulations?.custom_stipulations?.[2] || '',
              document_ids: [],
              servicingRate: (() => {
                const pct = (carve.stipulations?.servicing_rate_bps ?? 25) / 100;
                return {
                  isCustom: ![0.25, 0.5].includes(pct),
                  value: String(pct),
                  draftValue: [0.25, 0.5].includes(pct) ? '' : String(pct),
                };
              })(),
            },
          },
        }),
        {},
      );
      return newBidDetailsMap;
    });

    return carves;
  }, [carvesData, setBidDetailsMap, sellerParticipationRate]);

  const selectedCarve = useMemo(
    () => carves.find((carve) => carve.id === selectedCarveId),
    [carves, selectedCarveId],
  );

  useEffect(() => {
    if (!selectedCarveId && carves.length > 0) {
      setSelectedCarveId(carves[0].id);
    }
  }, [carves, selectedCarveId, setSelectedCarveId]);

  const onBidDetailsChanged = useCallback(
    (updatedDetails: BidDetails, name?: StipulationFieldName, value?: string | number | null) => {
      setBidDetailsMap((prev) => ({
        ...prev,
        [updatedDetails.carveId]: updatedDetails,
      }));
      if (name != null) {
        updateCarveStipulations({
          carveId: updatedDetails.carveId,
          fieldName: name,
          value: value != null ? value.toString() : null,
        });
      }
    },
    [updateCarveStipulations],
  );

  const updateCustomStips = useCallback(
    (carveId: string, customStipulations: string[]) => {
      setBidDetailsMap((prev) => ({
        ...prev,
        [carveId]: {
          ...prev[carveId],
          stipulations: {
            ...prev[carveId].stipulations,
            custom1: customStipulations[0] || '',
            custom2: customStipulations[1] || '',
            custom3: customStipulations[2] || '',
          },
        },
      }));
      updateCustomStipulations({
        carveId,
        customStipulations: customStipulations.filter(Boolean),
      });
    },
    [updateCustomStipulations, setBidDetailsMap],
  );

  const setStipFlag = useCallback(
    (fieldName: StipulationToggleName, value: boolean) => {
      if (!selectedCarveId) {
        return;
      }
      const isUncheckingServicingRetained =
        fieldName === StipulationToggleName.is_servicing_retained && !value;

      setBidDetailsMap((prev) => {
        const current = prev[selectedCarveId];
        const nextStipulations = {
          ...current.stipulations,
          [fieldName]: value,
          ...(isUncheckingServicingRetained && { transfer_amount: 100 }),
        };
        const nextParticipationPercent = isUncheckingServicingRetained
          ? { isCustom: false, value: '100', draftValue: '' }
          : current.participationPercent;
        return {
          ...prev,
          [selectedCarveId]: {
            ...current,
            stipulations: nextStipulations,
            participationPercent: nextParticipationPercent,
          },
        };
      });
      toggleCarveStipulation({
        carveId: selectedCarveId,
        fieldName,
        value: value,
      });
      if (isUncheckingServicingRetained) {
        updateCarveStipulationsImmediate({
          carveId: selectedCarveId,
          fieldName: StipulationFieldName.transfer_amount,
          value: '100',
        });
      }
    },
    [selectedCarveId, toggleCarveStipulation, updateCarveStipulationsImmediate],
  );

  return {
    carves,
    selectedCarve,
    selectedCarveId,
    setSelectedCarveId,
    bidDetailsMap,
    onBidDetailsChanged,
    updateCustomStips,
    carvesData,
    setStipFlag,
    flushCarveStipulations,
    flushCustomStipulations,
    updateCarveStipulationsImmediate,
  };
};

type BiddingAndPricingControllerProps = {
  dealId: string;
} & BiddingAndPricingControllerCard_PlaceBidCard;

const BiddingAndPricingController = (props: BiddingAndPricingControllerProps) => {
  const { dealId, assetClass, bids } = props;

  // Selected bid state for displaying bid details
  const [selectedBidId, setSelectedBidId] = useState<string>('');

  const {
    selectedCarve,
    selectedCarveId,
    setSelectedCarveId,
    bidDetailsMap,
    onBidDetailsChanged,
    updateCustomStips,
    carvesData,
    setStipFlag,
    flushCarveStipulations,
    flushCustomStipulations,
    updateCarveStipulationsImmediate,
  } = useCarvesAndBids(dealId, props.participation_rate);

  // Auto-select accepted bid (if available) or first bid when bids are available and none selected
  useEffect(() => {
    if (bids && bids.length > 0 && !selectedBidId) {
      // Prioritize accepted bid, fallback to first bid
      const acceptedBid = bids.find((bid) => bid.status === 'ACCEPTED');
      setSelectedBidId(acceptedBid?.bid_id || bids[0].bid_id);
    }
  }, [bids, selectedBidId]);

  // Find the selected bid object
  const selectedBid = selectedBidId ? bids?.find((bid) => bid.bid_id === selectedBidId) : null;

  // Helper functions for formatting
  const getFieldDisplayName = useCallback((fieldName: string) => {
    const config = Object.values(FIELD_CONFIG).find(
      (config) => config.filterSelector === fieldName,
    );
    return config?.string || fieldName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }, []);

  const getOperatorDisplayText = useCallback((operator: string) => {
    const operatorConfig = OPERATOR_CONFIG.find((config) => config.operator === operator);
    return operatorConfig?.operatorMiniString || operator;
  }, []);

  const currentBidDetails = selectedCarveId ? bidDetailsMap[selectedCarveId] : null;

  const useCashflowsSummary = getUseDealCashflows(
    assetClass,
    dealId,
    carvesData?.deal?.carves?.find((c) => c?.id === selectedCarveId)?.carve || [],
  );

  const onCashflowsSubmission = (validatedFormValues: CashFlowsValidValues) => {
    if (currentBidDetails) {
      const servicingValue = `${validatedFormValues.servicingRate}`;
      const servicingFloat = parseFloat(servicingValue);
      onBidDetailsChanged(
        {
          ...currentBidDetails,
          ...validatedFormValues,
          stipulations: {
            ...currentBidDetails.stipulations,
            servicingRate: {
              ...currentBidDetails.stipulations.servicingRate,
              isCustom: ![0.25, 0.5].includes(servicingFloat),
              value: servicingValue,
              draftValue: servicingValue,
            },
            servicing_rate_bps: Math.round(servicingFloat * 100),
          },
          delay: validatedFormValues.daysDelay,
        },
        StipulationFieldName.servicing_rate_bps,
        String(Math.round(servicingFloat * 100)),
      );
    }
  };

  const selectedCarveBidDetails = selectedCarve ? bidDetailsMap[selectedCarve.id] : undefined;

  const formValues = {
    cdr: currentBidDetails?.cdr || 0,
    cpr: currentBidDetails?.cpr || 0,
    daysDelay: currentBidDetails?.delay || 0,
    lsr: currentBidDetails?.lsr || 0,
    servicingRate: currentBidDetails
      ? parseFloat(currentBidDetails.stipulations.servicingRate.value)
      : 0,
    rateType: currentBidDetails?.rateType,
    defaultAssumptionType: currentBidDetails?.defaultAssumptionType || DefaultAssumptionType.CDR,
    prepaymentAssumptionType:
      currentBidDetails?.prepaymentAssumptionType || PrepaymentAssumptionType.CPR,
  };

  const cashFlowsDataDisplayProps = {
    onSubmit: onCashflowsSubmission,
    formValues,
    useSummaryData: useCashflowsSummary,
  };

  const bidDetailsList = Object.values(bidDetailsMap);

  const render = (
    <StyledBody>
      <DealRoomCard>
        <p>
          Select a carve, below, to price and save your bid. You may send one or more bids at a
          time.
        </p>
        <div className="flex justify-between">
          <div className="mb-4 mt-5 text-sm font-normal uppercase leading-tight text-slate-200">
            Bid carve selection
          </div>
          <BidActionButtons />
        </div>
        <CarveTable
          bidDetailsList={bidDetailsList}
          selectedCarveId={selectedCarveId}
          setSelectedCarveId={setSelectedCarveId}
        />
        <div className="mt-6">
          <Tabs>
            <Tab label="Yield Matrix">
              <YieldMatrix />
            </Tab>
            <Tab label="Bid Stipulations">
              <BidStipulations dealId={dealId} />
            </Tab>
            <Tab label="Cashflows">
              <CashFlowsFormWithDataDisplay
                onSubmit={cashFlowsDataDisplayProps.onSubmit}
                formValues={formValues}
                useSummaryData={cashFlowsDataDisplayProps.useSummaryData}
                withServicingRate
              />
            </Tab>
          </Tabs>
        </div>
      </DealRoomCard>

      {/* Buyer Bid History - Accordion Style */}
      {bids && bids.length > 0 && (
        <div className="mt-8">
          <DealRoomCard>
            <div className="mb-6">
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="text-xl font-semibold text-slate-100">Your Submitted Bids</h3>
                <p className="mt-2 text-sm text-gray-400">
                  Click on any bid to expand and view its details inline
                </p>
              </div>
            </div>

            <BuyerBidHistoryAccordion
              bids={bids.map(adaptPlaceBidCardBid)}
              selectedBid={selectedBid}
              dealId={dealId}
            />
          </DealRoomCard>
        </div>
      )}
    </StyledBody>
  );

  return (
    <BiddingPricingContext.Provider
      value={{
        dealId,
        updateBidDetails: onBidDetailsChanged,
        updateCustomStips,
        bidDetails: selectedCarveBidDetails,
        cashFlowsDataDisplayProps,
        bidCard: props,
        selectedCarve,
        setStipFlag,
        flushCarveStipulations,
        flushCustomStipulations,
        updateCarveStipulationsImmediate,
      }}
    >
      {render}
    </BiddingPricingContext.Provider>
  );
};

export default BiddingAndPricingController;
